/**
 * 🔴 **主题的服务端同步**（`AC-N3.2`「换设备 / 换浏览器后保持」的端侧半边）
 *
 * ## 这一层为什么必须存在
 *
 * `AppProvider` 的首屏主题来自**本地缓存**（防 FOUC）—— 但缓存**不是权威**：
 * 用户在新设备上打开时缓存里**什么都没有**，此时唯一能给出真实偏好的只有服务端。
 * 本 hook 就是"登录后把服务端值灌回 store"的那一根线。
 *
 * ## 🔴 与设置页面板共用同一个 query（`['preferences']`）
 *
 * 两处各取一次会造成**同一个人在两个地方看到不同主题**（缓存不同步）。共用一个 queryKey ⇒
 * 面板保存后 `invalidateQueries` 会**同时**刷新本 hook 拿到的值（一处写、两处读）。
 *
 * ## ⚠️ 死循环是怎么被挡住的
 *
 * `applyServerTheme` 的引用随 `state` 变化 ⇒ effect 会在每次 state 变化后重跑。
 * 若 reducer 对"同值"也返回**新对象**，就会「dispatch → 新 state → effect → dispatch」无限循环。
 * ⇒ `store.tsx` 的 `setTheme` 分支对同值**返回原引用**（无重渲染 ⇒ 链断）。两处必须成对存在。
 *
 * ## 🔴 **单靠上面那条不够** —— 批次 `94` 的真 bug（2026-10-21 负责人实测）
 *
 * 同值短路只挡得住"无限循环"，挡不住**回灌**：只要 `applyServerTheme` 的**引用**变了
 * （它原先在 `useMemo(..., [state, themeMode])` 里定义 ⇒ 每次 state 变化都换新），
 * 本 effect 就会重跑一次，把 **query 缓存里的旧服务端值**重新 dispatch 回去。
 *
 * 现象：系统深色 + 服务端偏好 `'system'` 时，点顶栏切换 → 状态变 `light` →
 * effect 立刻灌回 `'system'` → 解析回 `dark` ⇒ **"点了没反应、永远暗色"**。
 *
 * ⇒ 🔴 **前置条件（改这里之前先看）**：`applyServerTheme` 必须是**稳定引用**
 * （`AppProvider` 里已用 `useCallback([], dispatch)` 保证）。
 * 一旦有人把它改回 `useMemo`/内联箭头函数，本 effect 会再次退化成"每帧回灌"，
 * 而**类型检查与既有测试都不会报错** —— 只有人手点一次顶栏才会发现。
 *
 * ⚠️ 本 hook 只在**已登录**时取数（`enabled`）—— 未登录时 `GET /api/settings/preferences`
 * 会 401，白白触发一次"未授权 → 清会话"的路径。
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPreferences } from '../api/endpoints.js';
import { useApp } from './store.js';

export function useThemeSync(): void {
  const { session, applyServerTheme } = useApp();
  const q = useQuery({
    queryKey: ['preferences'],
    queryFn: getPreferences,
    enabled: session !== null,
  });

  /**
   * 🔴 **已经应用过的服务端值**（批次 `94` 防回灌的第二道闸）。
   *
   * 第一道闸是"`applyServerTheme` 必须是稳定引用"（见 `store.tsx`），但那道闸依赖
   * **别人不改坏** —— 而这类"改坏了也全绿"的隐性约束正是本项目反复踩的坑。
   * 本 ref 把防线放进本 hook **自己**手里：同一个服务端值**只应用一次**，
   * 之后无论 effect 因什么重跑，都不会把用户的本地选择顶掉。
   *
   * ⚠️ 语义边界：只在**服务端值发生变化**时应用（换设备改主题、设置页改主题都会变）。
   * "值没变但想重新应用"的场景不存在 —— 本地已经等于它了。
   */
  const appliedRef = useRef<string | null>(null);

  const serverTheme = q.data?.theme;
  useEffect(() => {
    if (serverTheme === undefined) return;
    if (appliedRef.current === serverTheme) return;
    appliedRef.current = serverTheme;
    applyServerTheme(serverTheme);
  }, [serverTheme, applyServerTheme]);
}
